import { useCallback } from 'react';
import {
  trackEvent,
  trackButtonClick,
  trackNavigation,
  trackFeatureUsage,
  trackError,
  trackUserInteraction,
  trackLead,
  trackDAppInteraction,
  trackIntegrationStep,
  trackDemoInteraction,
  trackCTAConversion,
  trackContentEngagement,
} from '@/lib/analytics';

export const useAnalytics = () => {
  const track = useCallback((eventName: string, properties?: Record<string, any>) => {
    trackEvent(eventName, properties);
  }, []);

  const trackButton = useCallback((buttonName: string, location?: string, additionalProperties?: Record<string, any>) => {
    trackButtonClick(buttonName, location, additionalProperties);
  }, []);

  const trackNav = useCallback((from: string, to: string) => {
    trackNavigation(from, to);
  }, []);

  const trackFeature = useCallback((featureName: string, action: string, additionalProperties?: Record<string, any>) => {
    trackFeatureUsage(featureName, action, additionalProperties);
  }, []);

  const trackErrorEvent = useCallback((errorMessage: string, errorLocation?: string, additionalProperties?: Record<string, any>) => {
    trackError(errorMessage, errorLocation, additionalProperties);
  }, []);

  const trackInteraction = useCallback((interactionType: string, element: string, additionalProperties?: Record<string, any>) => {
    trackUserInteraction(interactionType, element, additionalProperties);
  }, []);

  // New lead tracking functions
  const trackLeadGeneration = useCallback((leadType: 'cold' | 'warm' | 'hot', source: string, additionalProperties?: Record<string, any>) => {
    trackLead(leadType, source, additionalProperties);
  }, []);

  // dApp-specific tracking
  const trackDApp = useCallback((action: string, component: string, additionalProperties?: Record<string, any>) => {
    trackDAppInteraction(action, component, additionalProperties);
  }, []);

  const trackIntegration = useCallback((step: string, status: 'started' | 'completed' | 'failed', additionalProperties?: Record<string, any>) => {
    trackIntegrationStep(step, status, additionalProperties);
  }, []);

  const trackDemo = useCallback((demoType: string, action: string, additionalProperties?: Record<string, any>) => {
    trackDemoInteraction(demoType, action, additionalProperties);
  }, []);

  const trackCTA = useCallback((ctaText: string, ctaLocation: string, destination: string, conversionType: 'click' | 'signup' | 'demo_request') => {
    trackCTAConversion(ctaText, ctaLocation, destination, conversionType);
  }, []);

  const trackContent = useCallback((contentType: string, contentName: string, engagementType: string, additionalProperties?: Record<string, any>) => {
    trackContentEngagement(contentType, contentName, engagementType, additionalProperties);
  }, []);

  const result = {
    // Basic tracking
    track,
    trackButton,
    trackNav,
    trackFeature,
    trackError: trackErrorEvent,
    trackInteraction,
    
    // Lead & conversion tracking
    trackLead: trackLeadGeneration,
    trackCTA,
    
    // dApp-specific tracking
    trackDApp,
    trackIntegration,
    trackDemo,
    trackContent,
  };

  return result;
}; 